import { useMemo, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import RefreshIcon from "@mui/icons-material/Refresh";
import GridViewIcon from "@mui/icons-material/GridView";
import ViewListIcon from "@mui/icons-material/ViewList";
import SecurityIcon from "@mui/icons-material/Security";
import { api } from "../lib/api";
import { describeCheckError } from "../lib/check-error";
import { externalUrl, formatMs } from "../lib/format";
import { useAsync } from "../lib/hooks";
import { useI18n } from "../lib/i18n";
import type {
  AdminLog,
  BrandLink,
  Device,
  HealthStatus,
  LinkCheckResult,
  MatrixCell,
  MatrixLink,
} from "../lib/types";
import { Badge, Empty, ErrorBox, Loading, Stat } from "./ui";

export interface LinkAuditModalProps {
  link: BrandLink | { id: number; brand: string; domain: string; healthStatus?: HealthStatus };
  onClose: () => void;
}

interface SourceColumn {
  key: string;
  name: string;
  deviceId: number | null;
  region: string | null;
  network: string | null;
}

interface CheckRound {
  roundId: string;
  timestamp: string;
  timeMs: number;
  cells: Map<string, LinkCheckResult>;
  totalReported: number;
  healthyCount: number;
  unhealthyCount: number;
  verdict: "healthy" | "partial" | "down" | "unknown";
  failingNetworks: string[];
}

export function LinkAuditModal({ link, onClose }: LinkAuditModalProps) {
  const { t, fmtDate, fmtAgo } = useI18n();
  const [tab, setTab] = useState<"matrix" | "list" | "adminLogs">("matrix");
  const [limit, setLimit] = useState(100);
  const [statusFilter, setStatusFilter] = useState<"all" | "healthy" | "unhealthy">("all");
  const [sourceFilter, setSourceFilter] = useState<string>("all");
  const [selectedCell, setSelectedCell] = useState<LinkCheckResult | null>(null);

  // 1. Tải lịch sử kiểm tra của link
  const history = useAsync(
    () => api.deviceChecks.list({ linkId: link.id, limit }),
    [link.id, limit],
  );

  // 2. Tải ma trận hiện thời (all sources) của riêng link này
  const currentMatrix = useAsync(
    () => api.deviceChecks.matrix(60, link.id),
    [link.id],
  );

  // 3. Tải danh sách thiết bị để lấy tên/mạng đầy đủ
  const devices = useAsync(() => api.devices.list(), []);

  // 4. Tải Admin Audit Logs liên quan đến domain này
  const adminLogs = useAsync(
    () => api.adminLogs.list({ search: link.domain, limit: 50 }),
    [link.domain],
  );

  const rawResults: LinkCheckResult[] = history.data ?? [];
  const deviceList: Device[] = devices.data ?? [];
  const matrixLink: MatrixLink | undefined = currentMatrix.data?.links?.[0];

  const deviceMap = useMemo(() => {
    const map = new Map<number, Device>();
    for (const d of deviceList) {
      map.set(d.id, d);
    }
    return map;
  }, [deviceList]);

  // Lọc kết quả theo bộ lọc người dùng
  const filteredResults = useMemo(() => {
    return rawResults.filter((r) => {
      if (statusFilter !== "all" && r.healthStatus !== statusFilter) return false;
      if (sourceFilter !== "all") {
        if (sourceFilter === "server" && (r.deviceId !== null || r.source === "device")) return false;
        if (sourceFilter.startsWith("device:") && `device:${r.deviceId}` !== sourceFilter) return false;
      }
      return true;
    });
  }, [rawResults, statusFilter, sourceFilter]);

  // Thống kê tổng hợp
  const stats = useMemo(() => {
    const total = rawResults.length;
    const healthy = rawResults.filter((r) => r.healthStatus === "healthy").length;
    const unhealthy = rawResults.filter((r) => r.healthStatus === "unhealthy").length;
    const okMs = rawResults
      .filter((r) => r.healthStatus === "healthy" && typeof r.responseMs === "number")
      .map((r) => r.responseMs as number);
    const avgMs = okMs.length ? Math.round(okMs.reduce((a, b) => a + b, 0) / okMs.length) : null;
    const uptimeRate = total > 0 ? Math.round((healthy / total) * 1000) / 10 : 0;
    const latestCheck = rawResults[0]?.checkedAt ?? null;

    return { total, healthy, unhealthy, avgMs, uptimeRate, latestCheck };
  }, [rawResults]);

  // Xây dựng các cột nguồn (Columns) cho ma trận
  const sourceColumns = useMemo<SourceColumn[]>(() => {
    const cols: SourceColumn[] = [];
    const seenDeviceIds = new Set<number>();

    // Ưu tiên các thiết bị có trong dữ liệu check của link này
    for (const r of rawResults) {
      if (r.deviceId != null && !seenDeviceIds.has(r.deviceId)) {
        seenDeviceIds.add(r.deviceId);
        const dev = deviceMap.get(r.deviceId);
        cols.push({
          key: `device:${r.deviceId}`,
          name: dev?.name ?? r.deviceName ?? `Device #${r.deviceId}`,
          deviceId: r.deviceId,
          region: dev?.region ?? r.region,
          network: dev?.network ?? r.network,
        });
      }
    }

    // Bổ sung các thiết bị đang hoạt động nếu chưa có trong kết quả
    for (const dev of deviceList) {
      if (!seenDeviceIds.has(dev.id)) {
        seenDeviceIds.add(dev.id);
        cols.push({
          key: `device:${dev.id}`,
          name: dev.name,
          deviceId: dev.id,
          region: dev.region,
          network: dev.network,
        });
      }
    }

    return cols;
  }, [rawResults, deviceMap, deviceList]);

  // Gom các lần kiểm tra thành từng đợt (Check Rounds / Time clusters)
  const checkRounds = useMemo<CheckRound[]>(() => {
    if (!filteredResults.length) return [];

    // Nhóm các bản ghi trong vòng 90 giây vào cùng một đợt check
    const CLUSTER_WINDOW_MS = 90_000;
    const rounds: CheckRound[] = [];
    let currentRound: CheckRound | null = null;

    for (const r of filteredResults) {
      const timeMs = new Date(r.checkedAt).getTime();
      const sourceKey = r.deviceId == null ? "server" : `device:${r.deviceId}`;

      if (
        !currentRound ||
        Math.abs(currentRound.timeMs - timeMs) > CLUSTER_WINDOW_MS ||
        currentRound.cells.has(sourceKey)
      ) {
        if (currentRound) {
          rounds.push(currentRound);
        }
        currentRound = {
          roundId: `round-${timeMs}-${Math.random().toString(36).substring(2, 6)}`,
          timestamp: r.checkedAt,
          timeMs,
          cells: new Map([[sourceKey, r]]),
          totalReported: 1,
          healthyCount: r.healthStatus === "healthy" ? 1 : 0,
          unhealthyCount: r.healthStatus === "unhealthy" ? 1 : 0,
          verdict: "unknown",
          failingNetworks: [],
        };
      } else {
        currentRound.cells.set(sourceKey, r);
        currentRound.totalReported += 1;
        if (r.healthStatus === "healthy") currentRound.healthyCount += 1;
        if (r.healthStatus === "unhealthy") currentRound.unhealthyCount += 1;
      }
    }

    if (currentRound) {
      rounds.push(currentRound);
    }

    // Tính toán đồng thuận cho từng đợt
    for (const round of rounds) {
      const reported = round.totalReported;
      const healthy = round.healthyCount;
      const unhealthy = round.unhealthyCount;

      if (!reported) round.verdict = "unknown";
      else if (!unhealthy) round.verdict = "healthy";
      else if (!healthy) round.verdict = "down";
      else round.verdict = "partial";

      const fails: string[] = [];
      for (const cell of round.cells.values()) {
        if (cell.healthStatus === "unhealthy" && cell.network) {
          fails.push(cell.network);
        }
      }
      round.failingNetworks = Array.from(new Set(fails));
    }

    return rounds;
  }, [filteredResults]);

  function reloadAll() {
    history.reload();
    currentMatrix.reload();
    adminLogs.reload();
  }

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xl">
      {/* Header Modal */}
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1.5,
          borderBottom: 1,
          borderColor: "divider",
          pb: 1.5,
        }}
      >
        <Stack direction="row" spacing={1.5} sx={{ alignItems: "center", minWidth: 0, flexWrap: "wrap" }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {t("audit.modalTitle")}
          </Typography>
          <Chip size="small" variant="outlined" label={link.brand} sx={{ fontWeight: 600 }} />
          <Link
            href={externalUrl(link.domain)}
            target="_blank"
            rel="noreferrer noopener"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              fontFamily: "ui-monospace, Menlo, monospace",
              fontSize: 14,
              fontWeight: 600,
            }}
          >
            {link.domain}
            <OpenInNewIcon fontSize="inherit" />
          </Link>
          {link.healthStatus && <Badge status={link.healthStatus} />}
        </Stack>

        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
          <Tooltip title={t("common.refresh")}>
            <IconButton onClick={reloadAll} size="small" disabled={history.loading}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <IconButton onClick={onClose} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ p: 2.5, display: "flex", flexDirection: "column", gap: 2 }}>
        {/* Thanh thẻ tổng quan số liệu */}
        <Stack
          direction="row"
          spacing={1.5}
          useFlexGap
          sx={{ flexWrap: "wrap", "& > *": { flex: "1 1 180px" } }}
        >
          <Stat
            label={t("audit.uptimeRate")}
            value={`${stats.uptimeRate}%`}
            tone={stats.uptimeRate >= 95 ? "good" : stats.uptimeRate >= 80 ? "warn" : "bad"}
          />
          <Stat
            label={t("audit.avgLatency")}
            value={stats.avgMs != null ? `${stats.avgMs} ms` : "—"}
            tone="neutral"
          />
          <Stat
            label={t("audit.totalChecks")}
            value={`${stats.healthy} / ${stats.total}`}
            tone="neutral"
          />
          <Stat
            label={t("table.checkedAt")}
            value={stats.latestCheck ? fmtAgo(stats.latestCheck) : "—"}
            tone="neutral"
          />
        </Stack>

        {/* Trạng thái tức thời theo từng nguồn (Consensus hiện tại) */}
        {matrixLink && (
          <Paper
            variant="outlined"
            sx={{
              p: 1.5,
              bgcolor: "action.hover",
              borderRadius: 2,
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            <Stack
              direction="row"
              sx={{ justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1 }}
            >
              <Typography variant="caption" sx={{ fontWeight: 700, textTransform: "uppercase" }}>
                {t("audit.currentConsensus")}
              </Typography>
              <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                <Chip
                  size="small"
                  color={
                    matrixLink.consensus.verdict === "healthy"
                      ? "success"
                      : matrixLink.consensus.verdict === "down"
                        ? "error"
                        : matrixLink.consensus.verdict === "partial"
                          ? "warning"
                          : "default"
                  }
                  label={`${t(`verdict.${matrixLink.consensus.verdict}`)} (${matrixLink.consensus.healthy}/${matrixLink.consensus.total})`}
                />
                {matrixLink.consensus.avgResponseMs != null && (
                  <Typography variant="caption" color="text.secondary">
                    {formatMs(matrixLink.consensus.avgResponseMs)}
                  </Typography>
                )}
              </Stack>
            </Stack>

            <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap" }}>
              {matrixLink.cells.map((cell) => (
                <CurrentSourceChip
                  key={cell.key}
                  cell={cell}
                  onClick={() => {
                    const found = rawResults.find(
                      (r) => (r.deviceId == null ? "server" : `device:${r.deviceId}`) === cell.key,
                    );
                    if (found) setSelectedCell(found);
                  }}
                />
              ))}
            </Stack>
          </Paper>
        )}

        {/* Chuyển tab: Ma trận, Danh sách, Nhật ký Admin */}
        <Stack
          direction="row"
          sx={{
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1.5,
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <Tabs value={tab} onChange={(_e, v) => setTab(v)}>
            <Tab
              value="matrix"
              icon={<GridViewIcon fontSize="small" />}
              iconPosition="start"
              label={t("audit.tabMatrix")}
            />
            <Tab
              value="list"
              icon={<ViewListIcon fontSize="small" />}
              iconPosition="start"
              label={`${t("audit.tabList")} (${filteredResults.length})`}
            />
            <Tab
              value="adminLogs"
              icon={<SecurityIcon fontSize="small" />}
              iconPosition="start"
              label={`${t("audit.tabAdminLogs")} (${adminLogs.data?.total ?? 0})`}
            />
          </Tabs>

          {tab !== "adminLogs" && (
            <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
              {/* Lọc kết quả */}
              <TextField
                select
                size="small"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as any)}
                sx={{ minWidth: 120 }}
              >
                <MenuItem value="all">{t("common.all")}</MenuItem>
                <MenuItem value="healthy">{t("status.healthy")}</MenuItem>
                <MenuItem value="unhealthy">{t("status.unhealthy")}</MenuItem>
              </TextField>

              {/* Lọc nguồn */}
              <TextField
                select
                size="small"
                value={sourceFilter}
                onChange={(e) => setSourceFilter(e.target.value)}
                sx={{ minWidth: 150 }}
              >
                <MenuItem value="all">{t("audit.filterSource")}: {t("common.all")}</MenuItem>
                {sourceColumns.map((col) => (
                  <MenuItem key={col.key} value={col.key}>
                    {col.name} {col.network ? `(${col.network})` : ""}
                  </MenuItem>
                ))}
              </TextField>

              {/* Giới hạn số lượng */}
              <TextField
                select
                size="small"
                value={String(limit)}
                onChange={(e) => setLimit(Number(e.target.value))}
                sx={{ minWidth: 110 }}
              >
                {[30, 50, 100, 200, 500].map((n) => (
                  <MenuItem key={n} value={String(n)}>
                    {n} {t("audit.limit")}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
          )}
        </Stack>

        {history.error && <ErrorBox message={history.error} />}

        {history.loading ? (
          <Loading />
        ) : tab === "matrix" ? (
          /* TAB 1: DẠNG MA TRẬN (MATRIX VIEW) */
          <MatrixView
            rounds={checkRounds}
            columns={sourceColumns}
            onSelectCell={(res) => setSelectedCell(res)}
          />
        ) : tab === "list" ? (
          /* TAB 2: DẠNG DANH SÁCH CHI TIẾT (LIST VIEW) */
          <ListView results={filteredResults} onSelectCell={(res) => setSelectedCell(res)} />
        ) : (
          /* TAB 3: NHẬT KÝ THAO TÁC ADMIN (ADMIN LOGS) */
          <AdminLogsView
            logs={adminLogs.data?.items ?? []}
            loading={adminLogs.loading}
            error={adminLogs.error}
          />
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.5 }}>
        <Button onClick={onClose} variant="outlined">
          {t("common.close")}
        </Button>
      </DialogActions>

      {/* Modal chi tiết 1 ô kết quả khi click */}
      {selectedCell && (
        <CellDetailModal cell={selectedCell} onClose={() => setSelectedCell(null)} />
      )}
    </Dialog>
  );
}

/** Chip trạng thái hiện thời của từng nguồn */
function CurrentSourceChip({ cell, onClick }: { cell: MatrixCell; onClick?: () => void }) {
  const isHealthy = cell.healthStatus === "healthy";
  const isUnhealthy = cell.healthStatus === "unhealthy";
  const isMissing = !cell || cell.missing || cell.healthStatus == null;
  const name = cell.deviceName ?? (cell.deviceId == null ? "Server" : `Device #${cell.deviceId}`);

  const label = [
    name,
    cell.network ? `(${cell.network})` : null,
    cell.statusCode ? `· ${cell.statusCode}` : null,
    cell.responseMs != null ? `· ${formatMs(cell.responseMs)}` : null,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <Chip
      size="small"
      variant={isMissing ? "outlined" : "filled"}
      color={isHealthy ? "success" : isUnhealthy ? "error" : "default"}
      label={isMissing ? `${name}: ·` : label}
      onClick={onClick}
      sx={{ cursor: "pointer", fontWeight: 500 }}
    />
  );
}

/** Bảng ma trận theo thời gian × nguồn kiểm tra */
function MatrixView({
  rounds,
  columns,
  onSelectCell,
}: {
  rounds: CheckRound[];
  columns: SourceColumn[];
  onSelectCell: (res: LinkCheckResult) => void;
}) {
  const { t, fmtDate, fmtAgo } = useI18n();

  if (!rounds.length) {
    return <Empty message={t("audit.noData")} />;
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{ maxHeight: "55vh", overflow: "auto" }}
      >
        <Table size="small" stickyHeader sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, minWidth: 150, zIndex: 3 }}>
                {t("audit.roundTime")}
              </TableCell>
              <TableCell sx={{ fontWeight: 700, minWidth: 130, zIndex: 3 }}>
                {t("audit.consensus")}
              </TableCell>
              {columns.map((col) => (
                <TableCell
                  key={col.key}
                  align="center"
                  sx={{ fontWeight: 700, minWidth: 120, zIndex: 3 }}
                >
                  <Stack sx={{ alignItems: "center", lineHeight: 1.2 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>
                      {col.name}
                    </Typography>
                    {(col.network || col.region) && (
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                        {[col.region, col.network].filter(Boolean).join(" · ")}
                      </Typography>
                    )}
                  </Stack>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {rounds.map((round) => (
              <TableRow key={round.roundId} hover>
                {/* Cột thời gian */}
                <TableCell sx={{ whiteSpace: "nowrap" }}>
                  <Tooltip title={fmtDate(round.timestamp)}>
                    <Stack sx={{ lineHeight: 1.25 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 13 }}>
                        {new Date(round.timestamp).toLocaleTimeString()}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {fmtAgo(round.timestamp)}
                      </Typography>
                    </Stack>
                  </Tooltip>
                </TableCell>

                {/* Cột đồng thuận đợt check */}
                <TableCell sx={{ whiteSpace: "nowrap" }}>
                  <Tooltip
                    title={
                      round.failingNetworks.length
                        ? `${t("matrix.failingNetworks")}: ${round.failingNetworks.join(", ")}`
                        : ""
                    }
                  >
                    <Chip
                      size="small"
                      color={
                        round.verdict === "healthy"
                          ? "success"
                          : round.verdict === "down"
                            ? "error"
                            : round.verdict === "partial"
                              ? "warning"
                              : "default"
                      }
                      label={`${round.healthyCount}/${round.totalReported} ${t(`verdict.${round.verdict}`)}`}
                      sx={{ fontWeight: 600 }}
                    />
                  </Tooltip>
                </TableCell>

                {/* Các cột nguồn */}
                {columns.map((col) => {
                  const cell = round.cells.get(col.key);
                  return (
                    <TableCell
                      key={col.key}
                      align="center"
                      sx={{ p: 0.5, borderLeft: 1, borderColor: "divider" }}
                    >
                      <MatrixGridCell cell={cell} onClick={() => cell && onSelectCell(cell)} />
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="caption" color="text.secondary">
        {t("audit.clusterNote")}
      </Typography>
    </Box>
  );
}

/** Từng ô trong lưới ma trận */
function MatrixGridCell({
  cell,
  onClick,
}: {
  cell?: LinkCheckResult;
  onClick: () => void;
}) {
  const { t, fmtDate } = useI18n();

  if (!cell) {
    return (
      <Typography component="span" color="text.disabled" sx={{ fontSize: 16 }}>
        ·
      </Typography>
    );
  }

  const isHealthy = cell.healthStatus === "healthy";
  const isUnhealthy = cell.healthStatus === "unhealthy";

  const errorDesc = describeCheckError(
    t,
    { code: cell.errorCode, statusCode: cell.statusCode, detail: cell.errorDetail },
    cell.error,
  );

  const tooltipText = [
    `${cell.deviceName ?? (cell.deviceId == null ? "Server" : `Device #${cell.deviceId}`)}`,
    `${t("table.status")}: ${t(`status.${cell.healthStatus}`)}`,
    cell.statusCode != null ? `${t("table.http")}: ${cell.statusCode}` : null,
    cell.responseMs != null ? `${t("table.response")}: ${formatMs(cell.responseMs)}` : null,
    cell.ip ? `${t("table.checkerIp")}: ${cell.ip}` : null,
    errorDesc ? `${t("table.error")}: ${errorDesc}` : null,
    `${t("table.checkedAt")}: ${fmtDate(cell.checkedAt)}`,
  ]
    .filter(Boolean)
    .join("\n");

  return (
    <Tooltip title={tooltipText}>
      <Box
        onClick={onClick}
        sx={{
          display: "inline-flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          px: 1,
          py: 0.5,
          borderRadius: 1.5,
          cursor: "pointer",
          bgcolor: isHealthy ? "success.light" : isUnhealthy ? "error.light" : "action.hover",
          color: isHealthy ? "success.contrastText" : isUnhealthy ? "error.contrastText" : "text.primary",
          transition: "transform 0.1s, opacity 0.1s",
          "&:hover": {
            opacity: 0.85,
            transform: "scale(1.05)",
          },
          lineHeight: 1.1,
          minWidth: 55,
        }}
      >
        <Typography sx={{ fontSize: 12, fontWeight: 700 }}>
          {isHealthy ? "✅" : isUnhealthy ? "❌" : "❔"}{" "}
          {cell.statusCode ?? (cell.errorCode ? cell.errorCode.slice(0, 6) : "")}
        </Typography>
        {cell.responseMs != null && (
          <Typography sx={{ fontSize: 10, opacity: 0.9 }}>
            {formatMs(cell.responseMs)}
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
}

/** Bảng danh sách chi tiết (List View) */
function ListView({
  results,
  onSelectCell,
}: {
  results: LinkCheckResult[];
  onSelectCell: (res: LinkCheckResult) => void;
}) {
  const { t, fmtDate, fmtAgo } = useI18n();

  if (!results.length) {
    return <Empty message={t("audit.noData")} />;
  }

  return (
    <TableContainer
      component={Paper}
      variant="outlined"
      sx={{ maxHeight: "55vh", overflow: "auto" }}
    >
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700, minWidth: 140 }}>{t("table.time")}</TableCell>
            <TableCell sx={{ fontWeight: 700, minWidth: 150 }}>{t("table.source")}</TableCell>
            <TableCell sx={{ fontWeight: 700, minWidth: 110 }}>{t("table.status")}</TableCell>
            <TableCell sx={{ fontWeight: 700, minWidth: 80 }}>{t("table.http")}</TableCell>
            <TableCell sx={{ fontWeight: 700, minWidth: 90 }}>{t("table.response")}</TableCell>
            <TableCell sx={{ fontWeight: 700, minWidth: 120 }}>{t("table.checkerIp")}</TableCell>
            <TableCell sx={{ fontWeight: 700, minWidth: 200 }}>{t("table.error")}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {results.map((r) => {
            const err = describeCheckError(
              t,
              { code: r.errorCode, statusCode: r.statusCode, detail: r.errorDetail },
              r.error,
            );
            return (
              <TableRow
                key={r.id}
                hover
                onClick={() => onSelectCell(r)}
                sx={{ cursor: "pointer" }}
              >
                <TableCell sx={{ whiteSpace: "nowrap" }}>
                  <Tooltip title={fmtDate(r.checkedAt)}>
                    <Stack sx={{ lineHeight: 1.2 }}>
                      <Typography variant="body2" sx={{ fontSize: 12.5, fontWeight: 500 }}>
                        {fmtAgo(r.checkedAt)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(r.checkedAt).toLocaleTimeString()}
                      </Typography>
                    </Stack>
                  </Tooltip>
                </TableCell>

                <TableCell>
                  <Stack sx={{ lineHeight: 1.2 }}>
                    <Typography variant="body2" sx={{ fontSize: 12.5, fontWeight: 600 }}>
                      {r.deviceName ?? (r.deviceId == null ? "Server" : `Device #${r.deviceId}`)}
                    </Typography>
                    {(r.network || r.region) && (
                      <Typography variant="caption" color="text.secondary">
                        {[r.region, r.network].filter(Boolean).join(" · ")}
                      </Typography>
                    )}
                  </Stack>
                </TableCell>

                <TableCell>
                  <Badge status={r.healthStatus} />
                </TableCell>

                <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>
                  {r.statusCode ?? "—"}
                </TableCell>

                <TableCell sx={{ fontSize: 12 }}>
                  {formatMs(r.responseMs)}
                </TableCell>

                <TableCell sx={{ fontFamily: "monospace", fontSize: 11.5 }}>
                  {r.ip ?? "—"}
                </TableCell>

                <TableCell>
                  {err ? (
                    <Typography variant="caption" color="error.main" sx={{ display: "block" }}>
                      {err}
                    </Typography>
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      —
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

/** Xem lịch sử thao tác của Admin đối với link này */
function AdminLogsView({
  logs,
  loading,
  error,
}: {
  logs: AdminLog[];
  loading: boolean;
  error: string | null;
}) {
  const { t, fmtDate, fmtAgo } = useI18n();

  if (error) return <ErrorBox message={error} />;
  if (loading) return <Loading />;
  if (!logs.length) return <Empty message={t("audit.noAdminLogs")} />;

  return (
    <TableContainer
      component={Paper}
      variant="outlined"
      sx={{ maxHeight: "55vh", overflow: "auto" }}
    >
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700, minWidth: 140 }}>{t("table.time")}</TableCell>
            <TableCell sx={{ fontWeight: 700, minWidth: 120 }}>{t("adminlogs.user")}</TableCell>
            <TableCell sx={{ fontWeight: 700, minWidth: 120 }}>{t("table.ip")}</TableCell>
            <TableCell sx={{ fontWeight: 700, minWidth: 140 }}>{t("adminlogs.action")}</TableCell>
            <TableCell sx={{ fontWeight: 700, minWidth: 200 }}>{t("adminlogs.viewDetail")}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {logs.map((log) => (
            <TableRow key={log.id} hover>
              <TableCell sx={{ whiteSpace: "nowrap" }}>
                <Tooltip title={fmtDate(log.createdAt)}>
                  <Stack sx={{ lineHeight: 1.2 }}>
                    <Typography variant="body2" sx={{ fontSize: 12.5, fontWeight: 500 }}>
                      {fmtAgo(log.createdAt)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(log.createdAt).toLocaleTimeString()}
                    </Typography>
                  </Stack>
                </Tooltip>
              </TableCell>

              <TableCell sx={{ fontWeight: 600, fontSize: 12.5 }}>
                {log.username}
              </TableCell>

              <TableCell sx={{ fontFamily: "monospace", fontSize: 11.5 }}>
                {log.ip}
              </TableCell>

              <TableCell>
                <Chip
                  size="small"
                  variant="outlined"
                  label={log.action}
                  color={log.status === "FAILED" || (log.statusCode != null && log.statusCode >= 400) ? "error" : "primary"}
                />
              </TableCell>

              <TableCell>
                <Typography variant="caption" sx={{ display: "block", color: "text.secondary" }}>
                  {log.summary || log.path}
                </Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

/** Modal chi tiết một kết quả kiểm tra cụ thể */
function CellDetailModal({
  cell,
  onClose,
}: {
  cell: LinkCheckResult;
  onClose: () => void;
}) {
  const { t, fmtDate } = useI18n();

  const errorDesc = describeCheckError(
    t,
    { code: cell.errorCode, statusCode: cell.statusCode, detail: cell.errorDetail },
    cell.error,
  );

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {t("audit.detailTitle")}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
          <Badge status={cell.healthStatus} />
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            {cell.deviceName ?? (cell.deviceId == null ? "Server" : `Device #${cell.deviceId}`)}
          </Typography>
        </Stack>

        <Paper variant="outlined" sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1 }}>
          <DetailRow label={t("table.time")} value={fmtDate(cell.checkedAt)} />
          <DetailRow label={t("table.http")} value={cell.statusCode ?? "—"} />
          <DetailRow label={t("table.response")} value={formatMs(cell.responseMs)} />
          <DetailRow label={t("table.checkerIp")} value={cell.ip ?? "—"} />
          <DetailRow label={t("table.region")} value={cell.region ?? "—"} />
          <DetailRow label={t("table.network")} value={cell.network ?? "—"} />
          <DetailRow label={t("table.error")} value={errorDesc ?? "—"} />
          {cell.errorCode && <DetailRow label="Error Code" value={cell.errorCode} />}
          {cell.errorDetail && <DetailRow label="Error Detail" value={cell.errorDetail} />}
          {cell.error && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, display: "block" }}>
                Raw Error:
              </Typography>
              <Paper
                variant="outlined"
                sx={{
                  p: 1,
                  mt: 0.5,
                  bgcolor: "action.hover",
                  fontFamily: "monospace",
                  fontSize: 11,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-all",
                }}
              >
                {cell.error}
              </Paper>
            </Box>
          )}
        </Paper>
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.5 }}>
        <Button onClick={onClose} variant="contained">
          {t("common.close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "baseline", gap: 2 }}>
      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 110 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 500, wordBreak: "break-word" }}>
        {value}
      </Typography>
    </Stack>
  );
}
